import React, { useCallback, useEffect, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import makeStyles from '@mui/styles/makeStyles';
import { isEmpty } from 'lodash';
import { TimesheetLabel as Label } from 'modules/timesheet-labels/labelSchema';
import {
  LabelType,
  LabelValue,
  LabelValueAcceptedBy,
  LabelValueSource,
  TimeSignalAttribute,
  TimeSignalClassification
} from 'graphql/types';
import { useTimesheetLabelSuggestionContext } from 'modules/timesheet-labels';
import {
  useLabelSuggestion,
  mapDefaultValue,
  useUpdateDependentLabelsValueSourceOnSelection
} from 'modules/timesheet-labels/hooks';

import { useLabelsFormFieldContext } from 'modules/timesheet-labels';
import { FetchSuggestionsHandlerProps } from 'modules/notes/addNote/hooks';

import { useFeatureFlag } from 'modules/common/hooks';
import { NOTE_FIELDS } from '../notes/common';
import {
  OefChip,
  ClientChip,
  ProjectChip,
  TaskChip,
  TextChip,
  NumericChip,
  BillingChip
} from './components';
import { useIsSmartSuggestionSettingEnabled } from './hooks/useIsSmartSuggestionSettingEnabled';
import { useDependantLabels, determineLabelAcceptedBy } from './hooks';
import { ActivityChip } from './components/ActivityChip/ActivityChip';
import ProgramChip from './components/ProgramChip/ProgramChip';

const useStyles = makeStyles((theme) => ({
  container: {
    maxWidth: '49%',
    margin: theme.spacing(1, 0.5, 0, 0)
  },
  title: {
    color: theme.palette.grey[700]
  }
}));

export type TimesheetLabelProps = {
  label: Label;
  index: number;
  attributes?: TimeSignalAttribute[] | undefined;
  onLabelChange?: (props: FetchSuggestionsHandlerProps) => void;
};

export const hasLabelChanged = (
  val: LabelValue | null,
  finalSuggestions: LabelValue[] | undefined
): boolean => {
  const suggestedVal = finalSuggestions?.[0];

  if (!val && isEmpty(suggestedVal)) return false;

  if (val && !isEmpty(suggestedVal)) {
    if (
      val.idValue === suggestedVal?.idValue &&
      val.stringValue === suggestedVal?.stringValue &&
      val.numericValue === suggestedVal?.numericValue
    ) {
      return false;
    }
  }
  return true;
};

export const TimesheetLabel: React.FC<TimesheetLabelProps> = ({
  label,
  label: { id },
  index,
  attributes,
  onLabelChange
}: TimesheetLabelProps) => {
  const classes = useStyles();
  const {
    timesheetLabels: timesheetLabelsFormField
  } = useLabelsFormFieldContext();
  const fieldName = useMemo(() => {
    return `${timesheetLabelsFormField}.${index}.label`;
  }, [index, timesheetLabelsFormField]);
  const {
    labelSuggestions,
    disableSuggestions
  } = useTimesheetLabelSuggestionContext();
  const { getSuggestions } = useLabelSuggestion();
  const isSmartSuggestionEnabled =
    useIsSmartSuggestionSettingEnabled() && !disableSuggestions;
  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );

  const { setValue, trigger, getValues, control } = useFormContext();

  useEffect(() => {
    if (isSmartSuggestionEnabled && labelSuggestions) {
      const formLabel = getValues(fieldName);
      if (
        formLabel.valueSource === LabelValueSource.User ||
        formLabel.acceptedBy
      )
        return;
      const {
        labelValue: suggestedValues,
        metadata,
        thresholdMet
      } = getSuggestions(labelSuggestions, formLabel.id);
      const defaultValue = mapDefaultValue(formLabel, undefined);

      const finalSuggestions = suggestedValues
        ? suggestedValues
        : defaultValue
        ? [defaultValue]
        : undefined;

      if (
        hasLabelChanged(
          isTimeSignalFederatedSuggestionsEnabled
            ? formLabel?.suggestedValues?.[0]
            : formLabel?.value,
          finalSuggestions
        )
      ) {
        setValue(
          fieldName,
          {
            ...formLabel,
            value:
              !isTimeSignalFederatedSuggestionsEnabled && finalSuggestions
                ? finalSuggestions[0]
                : null,
            suggestedValues: suggestedValues,
            suggestionMetadata: metadata,
            valueSource: LabelValueSource.Suggestion,
            thresholdMet: thresholdMet,
            acceptedBy: determineLabelAcceptedBy(
              LabelValueSource.Suggestion,
              null,
              isSmartSuggestionEnabled
            )
          },
          { shouldValidate: true, shouldDirty: true }
        );
        trigger();
      }
    }
  }, [
    setValue,
    getValues,
    trigger,
    getSuggestions,
    isSmartSuggestionEnabled,
    labelSuggestions,
    fieldName,
    isTimeSignalFederatedSuggestionsEnabled
  ]);
  return (
    <div
      data-testid={`boundaryContainerTimesheetLabel-${id}`}
      className={classes.container}
    >
      <Controller
        data-testid={`controlled-chip-${label.id}`}
        render={useCallback(
          () => (
            <LabelComponent
              label={label}
              fieldName={fieldName}
              attributes={attributes}
              onLabelChange={onLabelChange}
            />
          ),
          [attributes, onLabelChange, fieldName, label]
        )}
        name={fieldName}
        control={control}
        defaultValue={label}
      />
    </div>
  );
};

export type LabelComponentProps = {
  label: Label;
  fieldName: string;
  attributes?: TimeSignalAttribute[] | undefined;
  onLabelChange?: (props: FetchSuggestionsHandlerProps) => void;
};

export const LabelComponent: React.FC<LabelComponentProps> = ({
  label,
  label: { type },
  fieldName,
  attributes,
  onLabelChange
}: LabelComponentProps) => {
  const { control, setValue, getValues, trigger } = useFormContext();
  const {
    timesheetLabels: timesheetLabelsFormField
  } = useLabelsFormFieldContext();
  const dirtyFields = useWatch({ name: fieldName, control });
  const noteClassification = useWatch({
    name: NOTE_FIELDS.CLASSIFICATION,
    control
  });
  const { disableSuggestions } = useTimesheetLabelSuggestionContext();

  const isSmartSuggestionEnabled =
    useIsSmartSuggestionSettingEnabled() && !disableSuggestions;
  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );
  const isEmptyLabelsEnabled = useFeatureFlag('isEmptyLabelsEnabled', false);

  const dependantLabels = useDependantLabels({ fieldName, label });
  const updateDependentLabelsValueSource = useUpdateDependentLabelsValueSourceOnSelection(
    dependantLabels,
    type,
    timesheetLabelsFormField,
    setValue
  );

  const onSelectValue = useCallback(
    (value?: LabelValue | null) => {
      const labelValue = {
        ...label,
        value: value
      };
      if (isSmartSuggestionEnabled) {
        const formLabel = getValues(fieldName);
        labelValue.suggestedValues =
          isTimeSignalFederatedSuggestionsEnabled && !value
            ? undefined
            : formLabel.suggestedValues;
        labelValue.suggestionMetadata =
          isTimeSignalFederatedSuggestionsEnabled && !value
            ? undefined
            : formLabel.suggestionMetadata;

        if (formLabel.valueSource === LabelValueSource.Suggestion && value) {
          //if different value then overwrite value source and accepted by
          if (
            value.idValue !== formLabel.value?.idValue ||
            value.stringValue !== formLabel.value?.stringValue ||
            value.numericValue !== formLabel.value?.numericValue
          ) {
            labelValue.valueSource = LabelValueSource.User;
            labelValue.acceptedBy = LabelValueAcceptedBy.User;
          } else {
            // else maintain the value source and accepted by
            labelValue.acceptedBy =
              formLabel.acceptedBy ?? LabelValueAcceptedBy.User;
            labelValue.valueSource =
              (formLabel.valueSource as LabelValueSource | undefined) ??
              LabelValueSource.User;
          }
        } else {
          //Legacy logic for setting valueSource when smart suggestion is enabled
          labelValue.valueSource = value
            ? LabelValueSource.User
            : LabelValueSource.Suggestion;
        }
      } else {
        //logic for setting valueSource when smart suggestion is disabled
        labelValue.valueSource = LabelValueSource.User;
      }
      labelValue.acceptedBy = determineLabelAcceptedBy(
        labelValue.valueSource,
        isEmptyLabelsEnabled &&
          !value &&
          noteClassification !== TimeSignalClassification.Accepted
          ? null
          : labelValue.acceptedBy,
        isSmartSuggestionEnabled
      );

      setValue(fieldName, labelValue, {
        shouldValidate: true,
        shouldDirty: true
      });

      if (isSmartSuggestionEnabled && value) {
        updateDependentLabelsValueSource();
      }
      trigger();
      if (
        label.type !== LabelType.Select &&
        label.type != LabelType.Numeric &&
        label.type != LabelType.Text
      ) {
        if (onLabelChange && value) onLabelChange({ attributes });
      }
    },
    [
      label,
      noteClassification,
      isSmartSuggestionEnabled,
      isEmptyLabelsEnabled,
      setValue,
      fieldName,
      trigger,
      getValues,
      isTimeSignalFederatedSuggestionsEnabled,
      updateDependentLabelsValueSource,
      onLabelChange,
      attributes
    ]
  );

  const taskIsDisabled = useMemo(() => {
    return Boolean(!dependantLabels.project?.label?.value?.idValue);
  }, [dependantLabels.project?.label?.value?.idValue]);

  const billingIsDisabled = useMemo(
    () => Boolean(!dependantLabels.project?.label?.value?.idValue),
    [dependantLabels.project?.label?.value?.idValue]
  );

  switch (type) {
    case LabelType.Client: {
      return (
        <ClientChip
          dependantLabels={dependantLabels}
          onSelect={onSelectValue}
        />
      );
    }
    case LabelType.Program: {
      return (
        <ProgramChip
          dependantLabels={dependantLabels}
          onSelect={onSelectValue}
        />
      );
    }
    case LabelType.Project: {
      return (
        <ProjectChip
          dependantLabels={dependantLabels}
          onSelect={onSelectValue}
        />
      );
    }
    case LabelType.Task: {
      return (
        <TaskChip
          dependantLabels={dependantLabels}
          onSelect={onSelectValue}
          disabled={taskIsDisabled}
        />
      );
    }
    case LabelType.Billing: {
      return (
        <BillingChip
          dependantLabels={dependantLabels}
          onSelect={onSelectValue}
          disabled={billingIsDisabled}
        />
      );
    }
    case LabelType.Activity: {
      return (
        <ActivityChip label={dirtyFields || label} onSelect={onSelectValue} />
      );
    }
    case LabelType.Select: {
      return <OefChip label={dirtyFields || label} onSelect={onSelectValue} />;
    }

    case LabelType.Text: {
      return <TextChip label={dirtyFields || label} onChange={onSelectValue} />;
    }

    case LabelType.Numeric: {
      return (
        <NumericChip label={dirtyFields || label} onChange={onSelectValue} />
      );
    }

    default:
      return <></>;
  }
};

export default TimesheetLabel;
